/**
 * QuizHost.jsx — Vue prof / vidéoprojecteur pour le Quiz d'ancrage en salle.
 * URL : /quiz/host/:code
 * Design : QR code géant + podium MotoGP évolutif en live.
 * Pas d'histogrammes — le focus est le classement.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Users, RefreshCw, Trophy, Crown, ScanLine, Flag, Zap,
  Volume2, VolumeX, LayoutGrid, Presentation, ChevronLeft, ChevronRight, Check,
  Sparkles,
} from 'lucide-react';
import { playPodiumUpdate, playNewChampion } from '../utils/quizChime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Palette 4 choix — identique à la vue élève
const CHOICE_STYLES = [
  { bg: 'from-red-600 to-red-700',       ring: 'ring-red-300',      shape: '▲' },
  { bg: 'from-blue-600 to-blue-700',     ring: 'ring-blue-300',     shape: '◆' },
  { bg: 'from-amber-500 to-orange-600',  ring: 'ring-amber-300',    shape: '●' },
  { bg: 'from-emerald-600 to-emerald-700', ring: 'ring-emerald-300', shape: '■' },
];

export default function QuizHost() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [prevParticipants, setPrevParticipants] = useState(0);
  const [prevAnswers, setPrevAnswers] = useState(0);
  const [pulseP, setPulseP] = useState(false);
  const [pulseA, setPulseA] = useState(false);
  const prevPodiumRef = useRef([]);
  const [flashRank1, setFlashRank1] = useState(false);
  // ▶ Son : off par défaut (contrainte navigateur : nécessite interaction)
  const [soundOn, setSoundOn] = useState(false);
  const soundOnRef = useRef(false);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);
  // ▶ Mode d'affichage : 'podium' (par défaut) ou 'focus' (une question à la fois)
  const [viewMode, setViewMode] = useState('podium');
  const [focusIndex, setFocusIndex] = useState(0);

  // ▶ Cérémonie AM : détecte le flag posé par CeremonieAM avant la redirection
  const [isCeremonie] = useState(() => {
    try { return sessionStorage.getItem('ceremonieReturn') === '1'; } catch { return false; }
  });

  const returnToCeremonie = () => {
    // Le flag sera supprimé côté CeremonieAM (via useEffect ?step=carte)
    navigate('/permis-am/ceremonie?step=carte');
  };

  const studentUrl = `${window.location.origin}/quiz/${code}`;

  useEffect(() => {
    let mounted = true;
    const fetchStats = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/quiz/sessions/${code}/stats`);
        if (!res.ok) throw new Error('Session introuvable');
        const data = await res.json();
        if (mounted) {
          setStats((prev) => {
            if (prev) {
              if (data.participants_count > prev.participants_count) {
                setPulseP(true);
                setTimeout(() => setPulseP(false), 900);
              }
              if (data.answers_count > prev.answers_count) {
                setPulseA(true);
                setTimeout(() => setPulseA(false), 900);
              }
              setPrevParticipants(prev.participants_count);
              setPrevAnswers(prev.answers_count);
            }
            // Flash quand le #1 change
            const newTop = data.podium?.find((p) => p.rank === 1)?.label;
            const oldTop = prevPodiumRef.current?.find((p) => p.rank === 1)?.label;
            if (newTop && newTop !== oldTop) {
              setFlashRank1(true);
              setTimeout(() => setFlashRank1(false), 1500);
              if (soundOnRef.current) playNewChampion();
            } else if (
              // Une évolution du top 3 (autre que #1) → tintement discret
              data.podium && prevPodiumRef.current &&
              JSON.stringify(data.podium.map((p) => p.label)) !==
              JSON.stringify(prevPodiumRef.current.map((p) => p.label))
            ) {
              if (soundOnRef.current) playPodiumUpdate();
            }
            prevPodiumRef.current = data.podium || [];
            return data;
          });
          setError(null);
        }
      } catch (e) {
        if (mounted) setError(e.message);
      }
    };
    fetchStats();
    const id = setInterval(fetchStats, 2500);
    return () => { mounted = false; clearInterval(id); };
  }, [code]);

  const closeSession = async () => {
    if (!window.confirm('Terminer le quiz et clôturer la session ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/quiz/sessions/${code}`, { method: 'DELETE' });
    } catch (_e) {
      // on ignore l'erreur : on veut de toute façon revenir en arrière
    }
    // Navigation forcée (rechargement complet) pour éviter le bug SPA
    // qui laissait l'écran noir après clôture de session.
    goBack();
  };

  // Retour sûr : essaie l'historique navigateur, retombe sur l'accueil si l'onglet a été ouvert directement.
  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  // Récupère les positions du podium (peut être vide au début)
  const podium = stats?.podium || [];
  const p1 = podium.find((p) => p.rank === 1);
  const p2 = podium.find((p) => p.rank === 2);
  const p3 = podium.find((p) => p.rank === 3);

  return (
    <div
      className="min-h-screen text-foreground p-3 sm:p-4 md:p-5 relative overflow-hidden"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1753563826116-714980c5ee31?fm=jpg&q=85&w=1920&auto=format&fit=crop')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#0a0a0a',
      }}
      data-testid="quiz-host-page"
    >
      {/* Vignette immersive — sombre uniquement au centre (lisibilité) tout en laissant l'image respirer */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(10,10,10,0.35) 0%, rgba(10,10,10,0.55) 55%, rgba(10,10,10,0.75) 100%)',
        }}
        aria-hidden="true"
      />

      {/* Accents lumineux rouge/ambre pour renforcer l'ambiance MotoGP */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-overlay"
        style={{
          background:
            'radial-gradient(ellipse at top left, rgba(239,68,68,0.35) 0%, transparent 45%), radial-gradient(ellipse at bottom right, rgba(245,158,11,0.28) 0%, transparent 50%)',
        }}
        aria-hidden="true"
      />

      {/* Damier motif très discret (piste) */}
      <div
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-conic-gradient(#ffffff 0% 25%, #000000 0% 50%)',
          backgroundSize: '30px 30px',
        }}
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto relative">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">
          <Button onClick={goBack} variant="ghost" size="sm" data-testid="quiz-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>

          {/* Toggle mode */}
          <div className="inline-flex rounded-xl border border-white/15 bg-black/40 p-1 backdrop-blur-sm">
            <button
              type="button"
              onClick={() => setViewMode('podium')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${
                viewMode === 'podium'
                  ? 'bg-red-600 text-white shadow-lg'
                  : 'text-foreground/70 hover:text-white hover:bg-white/5'
              }`}
              data-testid="mode-podium-btn"
              aria-pressed={viewMode === 'podium'}
            >
              <Trophy className="w-3.5 h-3.5" /> Podium
            </button>
            <button
              type="button"
              onClick={() => setViewMode('focus')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${
                viewMode === 'focus'
                  ? 'bg-red-600 text-white shadow-lg'
                  : 'text-foreground/70 hover:text-white hover:bg-white/5'
              }`}
              data-testid="mode-focus-btn"
              aria-pressed={viewMode === 'focus'}
            >
              <Presentation className="w-3.5 h-3.5" /> Focus question
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Toggle son */}
            <button
              type="button"
              onClick={() => setSoundOn((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest border transition-all ${
                soundOn
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                  : 'bg-white/5 border-white/15 text-foreground/70 hover:text-white'
              }`}
              data-testid="sound-toggle-btn"
              aria-pressed={soundOn}
              title={soundOn ? 'Couper le son' : 'Activer le son'}
            >
              {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              {soundOn ? 'Son' : 'Muet'}
            </button>

            <Button onClick={closeSession} variant="destructive" size="sm" data-testid="quiz-close-btn">
              Terminer le quiz
            </Button>

            {isCeremonie && (
              <Button
                onClick={returnToCeremonie}
                size="sm"
                className="bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-black font-black uppercase tracking-widest shadow-lg animate-pulse"
                data-testid="quiz-ceremonie-continue"
                title="Retour cérémonie · Générer la carte permis"
              >
                <Sparkles className="w-4 h-4 mr-1" /> Carte permis
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500 text-red-400 text-sm mb-4">
            {error}
          </div>
        )}

        {stats && viewMode === 'podium' && (
          <>
            {/* ═══════════════════ HERO COMPACT (1 ligne) ═══════════════════ */}
            <div className="flex flex-col lg:flex-row items-stretch gap-3 md:gap-4 mb-4 md:mb-5">
              {/* QR compact + code sur la même carte */}
              <div
                className="flex items-center gap-4 rounded-2xl bg-white p-3 md:p-4 shadow-2xl flex-shrink-0"
                data-testid="quiz-qr"
              >
                <div className="relative flex-shrink-0">
                  <QRCodeSVG value={studentUrl} size={140} level="M" />
                  <div className="absolute -top-1.5 -left-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-black uppercase tracking-widest shadow">
                    <ScanLine className="w-2.5 h-2.5" /> Live
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-[9px] font-black uppercase tracking-[0.22em] text-gray-500">
                    Code
                  </div>
                  <div
                    className="text-5xl md:text-6xl font-black tracking-[0.14em] text-gray-900 font-mono leading-none mt-0.5"
                  >
                    {code}
                  </div>
                  <div className="mt-1.5 text-[10px] text-gray-500 font-mono break-all max-w-[210px] leading-tight">
                    {studentUrl.replace(/^https?:\/\//, '')}
                  </div>
                </div>
              </div>

              {/* Bloc titre + compteurs alignés horizontalement */}
              <div className="flex-1 rounded-2xl border-2 border-red-500/40 bg-gradient-to-br from-red-950/50 via-black/45 to-orange-950/40 backdrop-blur-md p-3 md:p-4 flex items-center gap-4">
                {/* Titre compact */}
                <div className="flex-1 min-w-0">
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/40 text-[9px] md:text-[10px] font-black uppercase tracking-[0.22em] text-red-300">
                    <Flag className="w-2.5 h-2.5" /> {stats.quiz_title}
                  </div>
                  <h1
                    className="text-2xl md:text-3xl lg:text-4xl font-black leading-tight mt-1.5"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    Quiz en direct
                  </h1>
                </div>

                {/* Compteurs compacts */}
                <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                  {/* Pilotes */}
                  <div
                    className={`relative rounded-xl border-2 px-4 py-2 md:px-5 md:py-3 min-w-[100px] md:min-w-[120px] transition-all duration-500 ${
                      pulseP
                        ? 'border-red-400 bg-red-500/25 scale-[1.05] shadow-xl shadow-red-900/40'
                        : 'border-red-500/40 bg-red-500/8'
                    }`}
                  >
                    <div className="flex items-center gap-1 text-red-300 mb-0.5">
                      <Users className="w-3 h-3" />
                      <span className="text-[9px] font-black uppercase tracking-[0.22em]">
                        Pilotes
                      </span>
                    </div>
                    <div
                      className="text-4xl md:text-5xl font-black tabular-nums leading-none text-foreground"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                      data-testid="quiz-participants-count"
                    >
                      {stats.participants_count}
                    </div>
                    {pulseP && (
                      <div className="absolute -top-2 -right-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-black">
                        <Zap className="w-2.5 h-2.5" />
                        +{stats.participants_count - prevParticipants}
                      </div>
                    )}
                  </div>

                  {/* Réponses */}
                  <div
                    className={`relative rounded-xl border-2 px-4 py-2 md:px-5 md:py-3 min-w-[100px] md:min-w-[120px] transition-all duration-500 ${
                      pulseA
                        ? 'border-emerald-400 bg-emerald-500/25 scale-[1.05] shadow-xl shadow-emerald-900/40'
                        : 'border-emerald-500/40 bg-emerald-500/8'
                    }`}
                  >
                    <div className="flex items-center gap-1 text-emerald-300 mb-0.5">
                      <RefreshCw className="w-3 h-3" />
                      <span className="text-[9px] font-black uppercase tracking-[0.22em]">
                        Réponses
                      </span>
                    </div>
                    <div
                      className="text-4xl md:text-5xl font-black tabular-nums leading-none text-foreground"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                      data-testid="quiz-answers-count"
                    >
                      {stats.answers_count}
                    </div>
                    {pulseA && (
                      <div className="absolute -top-2 -right-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-black">
                        <Zap className="w-2.5 h-2.5" />
                        +{stats.answers_count - prevAnswers}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ═══════════════════ PODIUM MotoGP LIVE ═══════════════════ */}
            <MotoGPPodium p1={p1} p2={p2} p3={p3} flashRank1={flashRank1} />
          </>
        )}

        {/* ═══════════════════ MODE FOCUS QUESTION ═══════════════════ */}
        {stats && viewMode === 'focus' && (
          <FocusQuestion
            stats={stats}
            code={code}
            index={focusIndex}
            setIndex={setFocusIndex}
          />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FocusQuestion — Vue plein écran pour projection : 1 question à la fois
   avec grosses barres colorées en temps réel + navigation prev/next.
   ═══════════════════════════════════════════════════════════════════════════ */
function FocusQuestion({ stats, code, index, setIndex }) {
  const total = stats.per_question?.length || 0;
  const qs = stats.per_question?.[index];
  const questionText = qs?.q || `Question ${index + 1}`;
  const choices = qs?.choices?.length ? qs.choices : ['A', 'B', 'C', 'D'];

  if (!qs) {
    return (
      <div className="rounded-3xl border-2 border-white/10 bg-black/40 p-10 text-center">
        <div className="text-muted-foreground">Aucune donnée pour cette question.</div>
      </div>
    );
  }

  const totalVotes = qs.total || 0;
  const maxCount = Math.max(1, ...(qs.counts || []));

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(total - 1, i + 1));

  return (
    <div className="relative" data-testid="focus-question">
      {/* Fil d'ariane : code session compact + navigation */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 border border-white/15 text-xs font-mono">
          <ScanLine className="w-3.5 h-3.5 text-red-400" />
          <span className="text-red-300 font-black tracking-widest">{code}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-foreground/90">{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>
        </div>

        <div className="inline-flex items-center gap-2">
          <button
            type="button"
            onClick={prev}
            disabled={index === 0}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/15 text-sm font-black hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            data-testid="focus-prev-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Préc.
          </button>
          <div
            className="px-4 py-1.5 rounded-lg bg-red-600/20 border border-red-500/40 text-sm font-black tabular-nums"
            data-testid="focus-counter"
          >
            <span className="text-red-300">{index + 1}</span>
            <span className="text-muted-foreground"> / {total}</span>
          </div>
          <button
            type="button"
            onClick={next}
            disabled={index >= total - 1}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/15 text-sm font-black hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            data-testid="focus-next-btn"
          >
            Suiv. <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Question — grand format */}
      <div
        className="rounded-3xl border-2 border-red-500/25 bg-gradient-to-br from-red-950/40 via-card to-orange-950/25 p-8 md:p-10 mb-6 shadow-2xl"
        data-testid="focus-question-card"
      >
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-xs font-black uppercase tracking-[0.22em] text-red-300 mb-4">
          <Flag className="w-3 h-3" /> Question {index + 1}
        </div>
        <h2
          className="text-3xl md:text-5xl lg:text-6xl font-black leading-tight text-foreground"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {questionText}
        </h2>
      </div>

      {/* Grille 4 choix colorés */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {['A', 'B', 'C', 'D'].map((letter, i) => {
          const cnt = qs.counts?.[i] || 0;
          const pct = totalVotes > 0 ? Math.round((cnt / totalVotes) * 100) : 0;
          const isCorrect = qs.correct_answer === i;
          const style = CHOICE_STYLES[i];
          const relW = maxCount > 0 ? (cnt / maxCount) * 100 : 0;

          return (
            <div
              key={letter}
              className={`relative rounded-3xl overflow-hidden bg-gradient-to-br ${style.bg} min-h-[240px] md:min-h-[300px] flex flex-col p-4 md:p-6 transition-all ${
                isCorrect ? `ring-4 ${style.ring} shadow-2xl` : ''
              } ${totalVotes > 0 && !isCorrect ? 'opacity-80' : ''}`}
            >
              {/* Icône check bonne réponse */}
              {isCorrect && (
                <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-xl z-20 animate-in zoom-in-95 duration-300">
                  <Check className="w-6 h-6 text-emerald-600" strokeWidth={3.5} />
                </div>
              )}

              {/* Barre de remplissage verticale (bas → haut) */}
              <div
                className="absolute inset-x-0 bottom-0 bg-white/12 transition-all duration-500 ease-out"
                style={{ height: `${relW}%` }}
                aria-hidden="true"
              />

              {/* Contenu par-dessus */}
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-center justify-between text-white">
                  <span className="text-4xl md:text-6xl font-black leading-none">{letter}</span>
                  <span className="text-2xl md:text-4xl leading-none opacity-70" aria-hidden="true">
                    {style.shape}
                  </span>
                </div>

                <div className="flex-1" />

                <div className="text-white">
                  <div className="text-sm md:text-base font-bold leading-snug min-h-[3rem] line-clamp-3" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                    {choices[i]}
                  </div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <div
                      className="text-4xl md:text-6xl font-black tabular-nums leading-none"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      {pct}
                      <span className="text-2xl md:text-3xl">%</span>
                    </div>
                    <div className="text-xs md:text-sm font-bold opacity-90">
                      {cnt} vote{cnt !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Podium MotoGP — 3 blocs façon marches de podium avec projecteurs
   ═══════════════════════════════════════════════════════════════════════════ */
function MotoGPPodium({ p1, p2, p3, flashRank1 }) {
  return (
    <div className="relative" data-testid="quiz-podium">
      {/* Bannière Podium */}
      <div className="flex items-center justify-center gap-2 mb-4">
        <Flag className="w-5 h-5 text-red-400" />
        <div
          className="text-lg md:text-2xl font-black uppercase tracking-[0.28em] text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-amber-400 to-yellow-500"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Podium Live
        </div>
        <Flag className="w-5 h-5 text-red-400" />
      </div>

      {/* Damier au sol sous le podium */}
      <div className="relative">
        {/* Zone podium */}
        <div className="grid grid-cols-3 gap-3 md:gap-6 items-end max-w-5xl mx-auto min-h-[340px] md:min-h-[400px]">
          {/* #2 GAUCHE */}
          <PodiumStep
            rank={2}
            pilot={p2}
            heightClass="h-48 md:h-56"
            gradient="from-slate-300 via-slate-400 to-slate-600"
            glow="shadow-[0_0_45px_rgba(203,213,225,0.35)]"
            accent="text-slate-100"
          />
          {/* #1 CENTRE */}
          <PodiumStep
            rank={1}
            pilot={p1}
            heightClass="h-60 md:h-72"
            gradient="from-yellow-300 via-amber-400 to-yellow-600"
            glow="shadow-[0_0_75px_rgba(250,204,21,0.55)]"
            accent="text-yellow-100"
            isFirst
            flash={flashRank1}
          />
          {/* #3 DROITE */}
          <PodiumStep
            rank={3}
            pilot={p3}
            heightClass="h-40 md:h-48"
            gradient="from-orange-400 via-orange-500 to-orange-800"
            glow="shadow-[0_0_45px_rgba(251,146,60,0.35)]"
            accent="text-orange-100"
          />
        </div>

        {/* Bande de damier sous le podium (façon ligne d'arrivée) */}
        <div
          className="mt-2 h-3 md:h-4 rounded-b-2xl"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg, #ffffff 0 20px, #000000 20px 40px)',
            backgroundSize: '40px 100%',
          }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Une marche de podium (utilisé pour #1/#2/#3)
   ═══════════════════════════════════════════════════════════════════════════ */
function PodiumStep({ rank, pilot, heightClass, gradient, glow, accent, isFirst, flash }) {
  const hasPilot = !!pilot;

  return (
    <div className="relative flex flex-col items-center justify-end" data-testid={`quiz-podium-${rank}`}>
      {/* Motos animées (nouveau champion) */}
      {isFirst && hasPilot && flash && (
        <div className="absolute -top-16 md:-top-20 left-1/2 -translate-x-1/2 pointer-events-none z-20" aria-hidden="true">
          <div className="relative flex items-center">
            {/* Fumée / traînée */}
            <div className="c2r-moto-smoke absolute right-full mr-1 top-1/2 -translate-y-1/2 w-8 h-3 rounded-full bg-white/60 blur-md" />
            {/* Silhouette moto */}
            <MotoIcon className="c2r-moto-ride w-12 h-12 md:w-16 md:h-16 text-yellow-300 drop-shadow-[0_0_12px_rgba(250,204,21,0.85)]" />
          </div>
        </div>
      )}

      {/* Pilote (carte au-dessus de la marche) */}
      <div
        className={`w-full mb-2 md:mb-3 rounded-2xl border-2 p-2 md:p-3 text-center backdrop-blur-sm transition-all duration-500 ${
          hasPilot
            ? `${isFirst ? 'border-yellow-300/70 bg-gradient-to-b from-yellow-500/25 to-amber-600/10' : rank === 2 ? 'border-slate-300/60 bg-gradient-to-b from-slate-400/25 to-slate-600/10' : 'border-orange-400/60 bg-gradient-to-b from-orange-500/25 to-orange-700/10'}`
            : 'border-white/10 bg-black/25 border-dashed'
        } ${flash ? 'ring-4 ring-yellow-300/70 scale-[1.05]' : ''}`}
      >
        {isFirst && hasPilot && (
          <Crown
            className="w-6 h-6 md:w-8 md:h-8 text-yellow-300 mx-auto mb-0.5 drop-shadow-[0_0_10px_rgba(250,204,21,0.75)]"
            aria-hidden="true"
          />
        )}
        {!isFirst && hasPilot && (
          <Trophy className={`w-5 h-5 md:w-6 md:h-6 ${accent} mx-auto mb-0.5 opacity-90`} aria-hidden="true" />
        )}
        {hasPilot ? (
          <>
            <div
              className={`text-base md:text-xl font-black leading-tight truncate ${
                isFirst ? 'text-yellow-100' : 'text-foreground'
              }`}
              title={pilot.label}
            >
              {pilot.label}
            </div>
            <div className="mt-0.5 text-[9px] md:text-[10px] font-black uppercase tracking-widest text-foreground/70">
              <span className="text-foreground font-black text-xs md:text-sm">
                {pilot.correct}/{pilot.answered}
              </span>{' '}
              bonnes
            </div>
          </>
        ) : (
          <>
            <div className="text-xl md:text-2xl font-black text-white/25" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              —
            </div>
            <div className="mt-0.5 text-[9px] font-black uppercase tracking-widest text-white/40">
              En attente
            </div>
          </>
        )}
      </div>

      {/* Marche du podium */}
      <div
        className={`w-full ${heightClass} rounded-t-2xl relative overflow-hidden ${
          hasPilot ? glow : 'opacity-40 grayscale'
        } transition-all duration-700`}
      >
        {/* Corps métallique dégradé */}
        <div className={`absolute inset-0 bg-gradient-to-b ${gradient}`} />

        {/* Reflet vertical métallique */}
        <div
          className="absolute inset-y-0 left-0 w-1/3"
          style={{
            background: 'linear-gradient(90deg, rgba(255,255,255,0.25), transparent)',
          }}
          aria-hidden="true"
        />
        {/* Ombre à droite */}
        <div
          className="absolute inset-y-0 right-0 w-1/4"
          style={{
            background: 'linear-gradient(-90deg, rgba(0,0,0,0.35), transparent)',
          }}
          aria-hidden="true"
        />

        {/* Numéro rang géant */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div
            className={`text-[5rem] md:text-[8rem] font-black leading-none ${
              isFirst ? 'text-white' : 'text-white/95'
            } drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]`}
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {rank}
          </div>
          <div className={`mt-0.5 text-[10px] md:text-xs font-black uppercase tracking-[0.35em] ${
            isFirst ? 'text-white/90' : 'text-white/75'
          }`}>
            {isFirst ? 'Champion' : `Rang ${rank}`}
          </div>
        </div>

        {/* Bande damier en haut de la marche (bord supérieur) */}
        <div
          className="absolute top-0 inset-x-0 h-2 md:h-3"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg, #ffffff 0 12px, #000000 12px 24px)',
            backgroundSize: '24px 100%',
          }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   MotoIcon — silhouette d'une moto sportive vue de profil (roue avant → arrière)
   Utilisée pour l'animation « nouveau champion » sur le podium.
   ═══════════════════════════════════════════════════════════════════════════ */
function MotoIcon({ className = '' }) {
  return (
    <svg
      viewBox="0 0 64 40"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Roue arrière */}
      <circle cx="12" cy="30" r="7" />
      <circle cx="12" cy="30" r="1.5" fill="currentColor" />
      {/* Roue avant */}
      <circle cx="52" cy="30" r="7" />
      <circle cx="52" cy="30" r="1.5" fill="currentColor" />
      {/* Cadre principal (réservoir + selle) */}
      <path d="M18 22 L28 14 L42 14 L44 22" />
      {/* Réservoir bombé */}
      <path d="M28 14 Q34 10 40 14" fill="currentColor" fillOpacity="0.15" />
      {/* Fourche avant */}
      <path d="M42 14 L52 26" />
      {/* Guidon */}
      <path d="M42 14 L46 10" />
      {/* Bras oscillant arrière */}
      <path d="M18 22 L12 30" />
      {/* Selle */}
      <path d="M28 14 L22 20" strokeWidth="2.5" />
      {/* Pilote (buste stylisé) */}
      <path d="M30 12 L32 6 L38 4" strokeWidth="2.5" />
      <circle cx="38" cy="3" r="2" fill="currentColor" fillOpacity="0.9" />
    </svg>
  );
}
